import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseComponent } from '@core/components/base-component';
import { Estimate } from '@interfaces/Estimate';
import { InternalResource } from '@interfaces/InternalResource';
import { InternalResource_Estimate } from '@interfaces/InternalResource_Estimate';
import { InternalResourceVM } from '@models/InternalResourceVM';
import { TranslateService } from '@ngx-translate/core';
import { EstimateService } from '@shared/services/estimate.service';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { EMPTY, combineLatest, from, map, switchMap } from 'rxjs';

@Component({
  selector: 'pop-internal-resources-modal',
  templateUrl: './internal-resources-modal.component.html',
  providers: [ConfirmationService],
  styleUrls: ['./internal-resources-modal.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class InternalResourceModalComponent extends BaseComponent implements OnInit {
  public isLoading: boolean;
  public internalResources: InternalResource[] = [];
  public internalResourceVMs: InternalResourceVM[] = [];
  public internalResourceEstimates: Map<string, InternalResource_Estimate>;
  public currentEstimate: Estimate;

  public isFilteredByInternalResourceGroup = false;
  public isFilteredByInternalResourceFeeMethod = false;
  private internalResourceCodesInBillingMethod: string[] = [];
  public isGroupApproved: boolean;

  public constructor(
    private estimateService: EstimateService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService
  ) {
    super();
  }

  public ngOnInit() {
    this.isLoading = true;

    this.subscribe(
      combineLatest({
        estimate: this.estimateService.observeCurrentEstimate(),
        internalResources: this.estimateService.getInternalResources(),
        isGroupApproved: this.estimateService.observeIsCurrentEstimateGroupApproved()
      })
        .pipe(
          switchMap(results => {
            this.currentEstimate = results.estimate;
            this.internalResources = results.internalResources;
            this.isGroupApproved = results.isGroupApproved;
            this.createInternalResourceEstimatesMap(this.currentEstimate.internalResourceEstimates);

            return this.estimateService
              .getInternalFeeBillingMethods(this.currentEstimate.internalFeeBillingMethodCode)
              .pipe(map(methods => methods.map(method => method.resourceCode)));
          })
        )
        .subscribe(result => {
          this.internalResourceCodesInBillingMethod = result;
          this.createInternalResourceVMs(this.internalResources);
        })
    );
  }

  private createInternalResourceEstimatesMap(internalResourcesEstimate: InternalResource_Estimate[]) {
    this.internalResourceEstimates = new Map(
      internalResourcesEstimate.map(i => [i.internalResourceId, i] as [string, InternalResource_Estimate])
    );
  }

  private filterByBillingMethod(internalResources: InternalResource[]) {
    const isLimitInternalResourcesByBillingMethod = this.currentEstimate.limitInternalResourcesByBillingMethod;
    if (isLimitInternalResourcesByBillingMethod) {
      this.isFilteredByInternalResourceFeeMethod = true;
      return internalResources.filter(ir => this.internalResourceCodesInBillingMethod.includes(ir.code));
    }

    this.isFilteredByInternalResourceFeeMethod = false;
    return internalResources;
  }

  private filterByInternalResourceGroup(internalResources: InternalResource[]) {
    const estimateInternalResourceGroup = this.currentEstimate.internalResourceGroupId;

    if (estimateInternalResourceGroup) {
      this.isFilteredByInternalResourceGroup = true;
      return internalResources.filter(ir =>
        ir.internalResourceGroups.some(g => g.internalResourceGroupId === estimateInternalResourceGroup)
      );
    }

    this.isFilteredByInternalResourceGroup = false;
    return internalResources;
  }

  private createInternalResourceVMs(internalResources: InternalResource[]) {
    this.internalResourceVMs = [];

    const currentlyInEstimate = this.currentEstimate.internalResourceEstimates.map(ire => ire.internalResource.id);
    const keepInternalResources = internalResources.filter(i => currentlyInEstimate.includes(i.id));

    let filteredInternalResources = this.filterByInternalResourceGroup(internalResources);
    filteredInternalResources = this.filterByBillingMethod(filteredInternalResources);

    keepInternalResources.forEach(i => {
      if (filteredInternalResources.findIndex(f => f.id === i.id) === -1) {
        const internalResourceVM = this.createInternalResourceVM(i);
        internalResourceVM.isNotInActiveFilters = true;
      }
    });

    filteredInternalResources
      .sort((a, b) => a.code.localeCompare(b.code))
      .forEach(ir => this.createInternalResourceVM(ir));

    return (this.isLoading = false);
  }

  private createInternalResourceVM(internalResource: InternalResource) {
    const internalResourceVM: InternalResourceVM = new InternalResourceVM();
    const isSelected: boolean = this.internalResourceEstimates.has(internalResource.id);
    internalResourceVM.setInternalResourceVM(internalResource, isSelected);
    this.internalResourceVMs.push(internalResourceVM);
    return internalResourceVM;
  }

  saveChanges() {
    this.subscribe(this.estimateService.saveChanges().subscribe());
    this.estimateService.dialogEmitChanges();
  }

  public onSelect(input: HTMLInputElement, internalResourceVM: InternalResourceVM) {
    const isChecked = input.checked;

    if (isChecked) {
      this.addInternalResourceToEstimate(internalResourceVM);
    } else {
      this.confirmationService.confirm({
        message: this.translate.instant('ERROR.NODE_DELETE'),
        header: this.translate.instant('HEADER.REMOVE_NODE'),
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.translate.instant('BUTTON.CONFIRM'),
        rejectLabel: this.translate.instant('BUTTON.CANCEL'),
        accept: () => {
          input.checked = false;
          this.removeInternalResourceFromEstimate(internalResourceVM);
        },
        reject: () => {
          input.checked = true;
        }
      });
    }
  }

  private addInternalResourceToEstimate(internalResourceVM: InternalResourceVM) {
    const internalResourceExistsInEstimate = this.internalResourceEstimates.has(internalResourceVM.internalResource.id);
    if (!internalResourceExistsInEstimate) {
      this.estimateService.createInternalResourceEstimate(this.currentEstimate.id, internalResourceVM.internalResource);
    }
  }

  private removeInternalResourceFromEstimate(internalResourceVM: InternalResourceVM) {
    const internalResourceEstim = this.internalResourceEstimates.get(internalResourceVM.internalResource.id);

    // Breeze cannot perform cascade deletes, so instead of using Breeze setDeleted(),
    // we make a call to delete on the server, and once that is done we detach the entities from the Breeze cache

    if (internalResourceEstim) {
      const deleteInternalResourceObservable = this.estimateService
        .deleteInternalResourcesEstimate(internalResourceEstim.id)
        .pipe(
          map(response => {
            if (response) {
              return from(this.estimateService.detachEntity(internalResourceEstim));
            }
            return EMPTY;
          })
        );

      if (this.estimateService.entityManagerHasChanges()) {
        this.estimateService
          .saveChanges()
          .pipe(switchMap(() => deleteInternalResourceObservable))
          .subscribe();
      } else {
        deleteInternalResourceObservable.subscribe();
      }
    }
  }

  public filterTable(table: Table, eventTarget: EventTarget) {
    const input = eventTarget as HTMLInputElement;
    table.filterGlobal(input.value, 'contains');
  }
}
